import { Component, computed, inject, input, OnInit, signal } from "@angular/core";
import { TranslatePipe } from "@ngx-translate/core";
import { forkJoin, map, of, tap } from "rxjs";

import { Constants } from "../../../../../../constants/constants";
import { AdaaBoolean } from "../../../../../../constants/enums";
import type {
  CycleModelType,
  EntityModelType,
  InitiativeModelType,
  OrgUnit,
  UserModelType,
  WorkflowDiffVersionType,
} from "../../../../../../models";
import {
  CyclesApiService,
  EntitiesApiService,
  OrgUnitApiService,
  TpApiService,
  UsersApiService,
} from "../../../../../../services";
import {
  AccordionDiffViewComponent,
  AttachmentDiffViewComponent,
  LinkedNationalStrategiesDiffViewComponent,
  ListDiffViewComponent,
  NormalDiffViewComponent,
} from "../../_lib";
import {
  AccordionSectionConfigType,
  CompositeSectionConfigType,
  ListSectionConfigType,
} from "../../wf-difference.types";
import { commonTpScreenConfig } from "./tp-screen-config";

@Component({
  selector: "adaa-tp-screen",
  imports: [
    TranslatePipe,
    NormalDiffViewComponent,
    AttachmentDiffViewComponent,
    AccordionDiffViewComponent,
    ListDiffViewComponent,
    LinkedNationalStrategiesDiffViewComponent,
  ],
  templateUrl: "./tp-screen.html",
  styleUrl: "./tp-screen.scss",
})
export class TpScreenComponent implements OnInit {
  private readonly _tpApiService = inject(TpApiService);
  private readonly _usersApiService = inject(UsersApiService);
  private readonly _entitiesApiService = inject(EntitiesApiService);
  private readonly _cyclesApiService = inject(CyclesApiService);
  private readonly _orgUnitApiService = inject(OrgUnitApiService);

  showOnlyNew = input.required<boolean>();
  isNew = input<boolean>(false);
  newObject = input<WorkflowDiffVersionType>();
  oldObject = input<WorkflowDiffVersionType>();

  private readonly _newObject = computed<WorkflowDiffVersionType>(() => this.newObject() ?? {});
  private readonly _oldObject = computed<WorkflowDiffVersionType>(() => this.oldObject() ?? {});
  viewOnlyDifferences = input<boolean>(false);

  isScreenReady = signal<boolean>(false);
  cycles = signal<CycleModelType[]>([]);
  entities = signal<EntityModelType[]>([]);
  strategicProjects = signal<InitiativeModelType[]>([]);
  owners = signal<UserModelType[]>([]);
  orgUnits = signal<OrgUnit[]>([]);

  newData = computed(() => {
    let object = this._newObject();

    const owners = this.owners();
    if (owners && owners.length > 1) {
      if ("owner" in object) {
        const item = owners.find(({ id }) => id === object.owner);
        if (item) {
          object = {
            ...object,
            ownerNameAE: item.nameAE,
            ownerNameEN: item.nameEN,
          };
        }
      }

      if ("projectCoordinator" in object) {
        const item = owners.find(({ id }) => id === object.projectCoordinator);
        if (item) {
          object = {
            ...object,
            projectCoordinatorNameAE: item.nameAE,
            projectCoordinatorNameEN: item.nameEN,
          };
        }
      }
    }

    const orgUnits = this.orgUnits();
    if (orgUnits && orgUnits.length > 1) {
      if ("orgUnitId" in object) {
        const item = orgUnits.find(({ id }) => id === object.orgUnitId);
        if (!item) return object;

        object = {
          ...object,
          orgUnitNameAE: item.nameAE,
          orgUnitNameEN: item.nameEN,
        };
      }
    }

    return object;
  });
  oldData = computed(() => {
    let object = this._oldObject();

    const owners = this.owners();
    if (owners && owners.length > 1) {
      if ("owner" in object) {
        const item = owners.find(({ id }) => id === object.owner);
        if (item) {
          object = {
            ...object,
            ownerNameAE: item.nameAE,
            ownerNameEN: item.nameEN,
          };
        }
      }

      if ("projectCoordinator" in object) {
        const item = owners.find(({ id }) => id === object.projectCoordinator);
        if (item) {
          object = {
            ...object,
            projectCoordinatorNameAE: item.nameAE,
            projectCoordinatorNameEN: item.nameEN,
          };
        }
      }
    }

    const orgUnits = this.orgUnits();
    if (orgUnits && orgUnits.length > 1) {
      if ("orgUnitId" in object) {
        const item = orgUnits.find(({ id }) => id === object.orgUnitId);
        if (!item) return object;

        object = {
          ...object,
          orgUnitNameAE: item.nameAE,
          orgUnitNameEN: item.nameEN,
        };
      }
    }

    return object;
  });

  readonly config = commonTpScreenConfig;
  readonly $asAccordionConfig = ($s: CompositeSectionConfigType) => $s as AccordionSectionConfigType;
  readonly $asListConfig = ($s: CompositeSectionConfigType) => $s as ListSectionConfigType;

  readonly #entityIds = (): number[] => {
    const newEntityId = this._newObject().entityId;
    const oldEntityId = this._oldObject().entityId;
    const ids: number[] = [];

    if (newEntityId) ids.push(newEntityId);
    if (oldEntityId && oldEntityId !== newEntityId) ids.push(oldEntityId);

    return ids;
  };

  readonly #getOwners = () => {
    const entityIds = this.#entityIds();
    if (entityIds.length === 0) return of([]);

    const getOwnersPerEntity$ = entityIds.map((entityId) => this._usersApiService.getOwnersPerEntity(entityId));

    return forkJoin(getOwnersPerEntity$).pipe(
      map((res) => {
        const list = [];
        for (const { responseData } of res) {
          list.push(...(responseData ?? []));
        }
        return list;
      }),
      tap((data) => this.owners.set(data))
    );
  };
  readonly #getOrgUnits = () => {
    const entityIds = this.#entityIds();
    if (entityIds.length === 0) return of([]);

    const getbyEntityidAndObjectype$ = entityIds.map((entityId) =>
      this._orgUnitApiService.getbyEntityidAndObjectype(entityId, Constants.CONSTANT_PERMISSIONS.NATIONAL_PROJECTS)
    );

    return forkJoin(getbyEntityidAndObjectype$).pipe(
      map((res) => {
        const list = [];
        for (const { responseData } of res) {
          list.push(...(responseData ?? []));
        }
        return list;
      }),
      tap((data) => this.orgUnits.set(data))
    );
  };
  readonly #getStrategicProjects = () => {
    const entityIds = this.#entityIds();
    if (entityIds.length === 0) return of([]);

    const getStrategicProjects$ = entityIds.map((entityId) => this._tpApiService.getStrategicProjects(entityId));

    return forkJoin(getStrategicProjects$).pipe(
      map((res) => {
        const list = [];
        for (const { responseData } of res) {
          list.push(...(responseData ?? []));
        }
        return list;
      }),
      tap((data) => this.strategicProjects.set(data))
    );
  };
  readonly #apiRequests = () => {
    const api$ = [this._cyclesApiService.getAll(), this._entitiesApiService.getAll(AdaaBoolean.Y, AdaaBoolean.N)];
    const obs$ = forkJoin(api$).pipe(
      tap({
        next: ([cycles, entities]) => {
          this.cycles.set(cycles.responseData as CycleModelType[]);
          this.entities.set(entities.responseData as EntityModelType[]);
        },
      })
    );

    return forkJoin([obs$, this.#getOwners(), this.#getOrgUnits(), this.#getStrategicProjects()]).subscribe({
      next: () => {
        this.isScreenReady.set(true);
      },
    });
  };

  public ngOnInit() {
    this.#apiRequests();
  }
}
